import React, { useState, useMemo, useEffect } from 'react';
import { X, Plus, Trash2, DollarSign, Mail } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { adminDialogPanel, adminOverlay } from '@/components/admin/adminUi';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const emptyPartRow = () => ({ id: crypto.randomUUID(), name: '', price: '' });

const formatUsd = (n) => {
  const val = Number(n) || 0;
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val);
};

export default function ServiceQuoteModal({
  requestId,
  request,
  token,
  onClose,
  onSuccess,
}) {
  const headers = { Authorization: `Bearer ${token}` };
  const [parts, setParts] = useState([emptyPartRow()]);
  const [hourlyLaborRate, setHourlyLaborRate] = useState('');
  const [hoursRequired, setHoursRequired] = useState('');
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [showSendStep, setShowSendStep] = useState(false);
  const [customerEmail, setCustomerEmail] = useState(request?.email || '');

  useEffect(() => {
    const existing = request?.service_quote;
    if (!existing) return;
    if (existing.parts?.length) {
      setParts(existing.parts.map((p) => ({
        id: crypto.randomUUID(),
        name: p.name || '',
        price: p.price != null ? String(p.price) : '',
      })));
    }
    if (existing.hourly_labor_rate != null) setHourlyLaborRate(String(existing.hourly_labor_rate));
    if (existing.hours_required != null) setHoursRequired(String(existing.hours_required));
  }, [request]);

  const totalLaborCharge = useMemo(() => {
    const rate = parseFloat(hourlyLaborRate) || 0;
    const hours = parseFloat(hoursRequired) || 0;
    return rate * hours;
  }, [hourlyLaborRate, hoursRequired]);

  const totalPartsCharge = useMemo(() => (
    parts.reduce((sum, row) => sum + (parseFloat(row.price) || 0), 0)
  ), [parts]);

  const totalServiceCharge = useMemo(
    () => totalPartsCharge + totalLaborCharge,
    [totalPartsCharge, totalLaborCharge],
  );

  const inputClass = "w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50";
  const labelClass = "block text-xs font-['Manrope'] text-white/60 mb-1.5";

  const buildPayload = () => {
    const validParts = parts
      .map((row) => ({ name: row.name.trim(), price: parseFloat(row.price) || 0 }))
      .filter((p) => p.name);
    if (!validParts.length) {
      toast.error('Add at least one part with a name and price');
      return null;
    }
    const rate = parseFloat(hourlyLaborRate);
    const hours = parseFloat(hoursRequired);
    if (!rate || rate <= 0) {
      toast.error('Hourly labor rate is required');
      return null;
    }
    if (!hours || hours <= 0) {
      toast.error('Hours required is required');
      return null;
    }
    return {
      parts: validParts,
      hourly_labor_rate: rate,
      hours_required: hours,
    };
  };

  const handleAddPartRow = () => {
    setParts((prev) => [...prev, emptyPartRow()]);
  };

  const handleRemovePartRow = (id) => {
    setParts((prev) => (prev.length > 1 ? prev.filter((r) => r.id !== id) : prev));
  };

  const handleSave = async () => {
    const payload = buildPayload();
    if (!payload) return;
    setSaving(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/service-requests/${requestId}/quote/save`,
        payload,
        { headers },
      );
      toast.success('Quote saved');
      onSuccess?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save quote');
    } finally {
      setSaving(false);
    }
  };

  const handleSend = async () => {
    const payload = buildPayload();
    if (!payload) return;
    if (!customerEmail.trim()) {
      toast.error('Customer email is required');
      return;
    }
    setSending(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/service-requests/${requestId}/quote/send`,
        { ...payload, customer_email: customerEmail.trim() },
        { headers },
      );
      toast.success('Quote emailed');
      onSuccess?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to email quote');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className={adminOverlay('z-[70]')} data-testid="service-quote-modal">
      <div className={adminDialogPanel('max-w-2xl')}>
        <div className="flex items-center justify-between p-5 border-b border-white/10 sticky top-0 bg-[#0a0a0a] z-10">
          <div>
            <h2 className="text-lg font-['Unbounded'] font-bold text-white">Add Service Quote</h2>
            <p className="text-xs font-['Manrope'] text-white/40 mt-0.5">{request?.name}</p>
          </div>
          <button type="button" onClick={onClose} className="text-white/50 hover:text-white p-1">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          <div>
            <label className={labelClass}>Parts to be replaced</label>
            <div className="space-y-2">
              {parts.map((row, index) => (
                <div key={row.id} className="flex gap-2 items-start">
                  <div className="flex-1">
                    <input
                      type="text"
                      placeholder="Part about to be replaced"
                      value={row.name}
                      onChange={(e) => setParts((prev) => prev.map((r) => (
                        r.id === row.id ? { ...r, name: e.target.value } : r
                      )))}
                      className={inputClass}
                      data-testid={index === 0 ? 'quote-part-name' : undefined}
                    />
                  </div>
                  <div className="w-32">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="Price"
                      value={row.price}
                      onChange={(e) => setParts((prev) => prev.map((r) => (
                        r.id === row.id ? { ...r, price: e.target.value } : r
                      )))}
                      className={inputClass}
                      data-testid={index === 0 ? 'quote-part-price' : undefined}
                    />
                  </div>
                  {index === parts.length - 1 ? (
                    <button
                      type="button"
                      onClick={handleAddPartRow}
                      className="p-2.5 border border-white/10 text-blue-400 hover:bg-white/5 shrink-0"
                      title="Add another part"
                      data-testid="add-part-row-btn"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => handleRemovePartRow(row.id)}
                      className="p-2.5 border border-white/10 text-red-400/70 hover:bg-white/5 shrink-0"
                      title="Remove part"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className={labelClass}>Hourly Labor Rate *</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={hourlyLaborRate}
                onChange={(e) => setHourlyLaborRate(e.target.value)}
                className={inputClass}
                data-testid="quote-labor-rate"
              />
            </div>
            <div>
              <label className={labelClass}>Hours Required *</label>
              <input
                type="number"
                min="0"
                step="0.25"
                value={hoursRequired}
                onChange={(e) => setHoursRequired(e.target.value)}
                className={inputClass}
                data-testid="quote-hours-required"
              />
            </div>
            <div>
              <label className={labelClass}>Total Labor Charge</label>
              <div className="w-full bg-white/[0.03] border border-white/10 text-emerald-300 text-sm px-3 py-2.5 font-['Manrope'] font-semibold">
                {formatUsd(totalLaborCharge)}
              </div>
            </div>
          </div>

          <div className="bg-blue-500/10 border border-blue-500/25 p-4 flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm font-['Manrope'] text-white/70">
              <DollarSign className="w-4 h-4 text-blue-400" />
              Quote total
            </div>
            <span className="text-xl font-['Unbounded'] font-bold text-white" data-testid="quote-total">
              {formatUsd(totalServiceCharge)}
            </span>
          </div>

          {showSendStep && (
            <div className="border border-white/10 p-4 bg-white/[0.02]">
              <label className={labelClass}>Customer Email *</label>
              <input
                type="email"
                value={customerEmail}
                onChange={(e) => setCustomerEmail(e.target.value)}
                placeholder="customer@email.com"
                className={inputClass}
                data-testid="quote-send-email"
              />
              <p className="text-[11px] font-['Manrope'] text-white/35 mt-2">
                The customer will receive a link to review this quote, agree to the price, and confirm with their full legal name.
              </p>
            </div>
          )}
        </div>

        <div className="flex flex-col sm:flex-row justify-end gap-3 p-5 border-t border-white/10">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-white/10 text-white/70 text-sm font-['Manrope'] hover:bg-white/5"
          >
            Cancel
          </button>
          {!showSendStep ? (
            <>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                data-testid="add-service-charge-btn"
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-['Manrope'] font-semibold disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save quote'}
              </button>
              <button
                type="button"
                onClick={() => {
                  const payload = buildPayload();
                  if (payload) setShowSendStep(true);
                }}
                data-testid="send-service-quote-btn"
                className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm font-['Manrope'] font-semibold"
              >
                <Mail className="w-4 h-4" />
                Email quote
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={() => setShowSendStep(false)}
                className="px-4 py-2 border border-white/10 text-white/70 text-sm font-['Manrope'] hover:bg-white/5"
              >
                Back
              </button>
              <button
                type="button"
                onClick={handleSend}
                disabled={sending}
                data-testid="confirm-send-quote-btn"
                className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm font-['Manrope'] font-semibold disabled:opacity-50"
              >
                <Mail className="w-4 h-4" />
                {sending ? 'Sending...' : 'Email quote'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}